import axios from 'axios';
import { getAuthHeader } from './authToken';

/**
 * Error thrown when a regen job ends in a non-success state. Carries the
 * server-provided `code` (e.g. 'NO_FRESH_STORIES') so callers can special-case.
 */
export class RegenJobError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'RegenJobError';
        this.code = code || null;
    }
}

/**
 * Poll a per-asset newsletter regeneration job until it finishes, then resolve
 * with its result payload.
 *
 * The regen endpoints (regenerate-story / -story-image / -hero-image) now return
 * `202 { job_id }` immediately and do the heavy AI work in a background job.
 * This polls GET /newsletters/regen-status/:jobId until the job completes.
 *
 * @param {string} jobId
 * @param {object} [opts]
 * @param {number} [opts.intervalMs=3000] - delay between polls
 * @param {number} [opts.timeoutMs=240000] - overall cap before giving up
 * @param {AbortSignal} [opts.signal] - abort polling (e.g. on unmount)
 * @returns {Promise<object>} the job's `result` payload (e.g. { image_url })
 * @throws {RegenJobError} on failed jobs, business errors (with `code`), timeout, or abort
 */
export async function pollRegenJob(jobId, opts = {}) {
    const { intervalMs = 3000, timeoutMs = 240000, signal } = opts;
    const startedAt = Date.now();

    const sleep = (ms) => new Promise((resolve, reject) => {
        if (signal?.aborted) return reject(new RegenJobError('Aborted', 'ABORTED'));
        const t = setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
            clearTimeout(t);
            reject(new RegenJobError('Aborted', 'ABORTED'));
        }, { once: true });
    });

    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (signal?.aborted) throw new RegenJobError('Aborted', 'ABORTED');
        if (Date.now() - startedAt > timeoutMs) {
            throw new RegenJobError('Timed out waiting for generation to finish', 'TIMEOUT');
        }

        const { data } = await axios.get(
            `${process.env.REACT_APP_URL}/api/v1/newsletters/regen-status/${jobId}`,
            { headers: { Authorization: getAuthHeader() } }
        );

        if (data.state === 'completed') {
            const result = data.result || {};
            if (result.ok === false) {
                throw new RegenJobError(result.error || 'Generation failed', result.code);
            }
            return result;
        }
        if (data.state === 'failed') {
            throw new RegenJobError(data.error || 'Generation failed', data.code);
        }

        await sleep(intervalMs);
    }
}
